import { useState } from "react";

import {
  useHospitalDetail,
  useHospitalWards,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { ErrorMessage, LoadingOverlay, Tab, TabsContainer } from "../../shared";
import { HospitalBulkImportTab } from "./HospitalBulkImportTab";
import { HospitalCustomFieldsTab } from "./HospitalCustomFieldsTab";
import { TabStack } from "./HospitalDetails.styles";
import { HospitalHistoryTab } from "./HospitalHistoryTab";
import { HospitalPatientsTab } from "./HospitalPatientsTab";
import { HospitalRecordForm } from "./HospitalRecordForm";
import { HospitalStaffTab } from "./HospitalStaffTab";
import { HospitalWardsTab } from "./HospitalWardsTab";

export type IHospitalDetailTab =
  | "hospital"
  | "wards"
  | "patients"
  | "staff"
  | "customFields"
  | "bulkImport"
  | "history";

const TABS: { id: IHospitalDetailTab; label: string }[] = [
  { id: "hospital", label: "Hospital" },
  { id: "wards", label: "Wards" },
  { id: "patients", label: "Patients" },
  { id: "staff", label: "Staff" },
  { id: "customFields", label: "Custom fields" },
  { id: "bulkImport", label: "Bulk import" },
  { id: "history", label: "History" },
];

export interface IHospitalDetailsPanelProps {
  hospitalId: number;
  initialTab?: IHospitalDetailTab;
}

/**
 * One hospital's record, wards, patients, staff, custom fields, imports and
 * audit trails — the portal port of Retool's `page_HospitalDetails`
 * (EN-5172 + EN-5175).
 *
 * ⚠️ Behind these tabs an operator can create patients and staff accounts,
 * change a hospital's feature flags for everyone in it, and import patients in
 * bulk. Every write is behind a confirmation that names its subject.
 *
 * **Seven tabs, not five stacked containers.** Retool put the hospital form and
 * four tables on one screen with three drawers over the top, so the hospital's
 * own identity scrolled away and the drawers hid the row that opened them. The
 * tabs group by subject, and the page title above them names the hospital on
 * every one.
 *
 * The wards list loads once here and is passed down: the patients, staff and
 * bulk-import tabs all need it (to choose a ward), and Retool re-fetched it per
 * container.
 */
export function HospitalDetailsPanel({
  hospitalId,
  initialTab = "hospital",
}: IHospitalDetailsPanelProps) {
  const [tab, setTab] = useState<IHospitalDetailTab>(initialTab);

  const { hospital, isLoading, isError } = useHospitalDetail(hospitalId);
  const {
    wards,
    isLoading: wardsLoading,
    isError: wardsError,
  } = useHospitalWards(hospitalId);

  if (isLoading) {
    return <LoadingOverlay>Loading hospital...</LoadingOverlay>;
  }

  if (isError || !hospital) {
    return (
      <ErrorMessage>
        Failed to load this hospital. Try refreshing the page.
      </ErrorMessage>
    );
  }

  return (
    <TabStack>
      <TabsContainer>
        {TABS.map((entry) => (
          <Tab
            key={entry.id}
            type="button"
            active={tab === entry.id}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </Tab>
        ))}
      </TabsContainer>

      {tab === "hospital" && <HospitalRecordForm hospital={hospital} />}

      {tab === "wards" && (
        <HospitalWardsTab
          hospital={hospital}
          wards={wards}
          isLoading={wardsLoading}
          isError={wardsError}
        />
      )}

      {tab === "patients" && (
        <HospitalPatientsTab hospital={hospital} wards={wards} />
      )}

      {tab === "staff" && (
        <HospitalStaffTab hospital={hospital} wards={wards} />
      )}

      {tab === "customFields" && (
        <HospitalCustomFieldsTab hospital={hospital} />
      )}

      {tab === "bulkImport" && (
        <HospitalBulkImportTab hospital={hospital} wards={wards} />
      )}

      {tab === "history" && <HospitalHistoryTab hospital={hospital} />}
    </TabStack>
  );
}
